import { useState, useEffect, useCallback, useRef } from 'react';
import { docuwareFilingCheckApi } from '../services/docuwareFilingCheckApi';
import type {
    FilingCheckRow, FilingCheckSummary, FilingCheckFilters, FilingCheckSortField,
} from '../types/docuwareFilingCheck';
import { EMPTY_FILING_CHECK_FILTERS } from '../types/docuwareFilingCheck';

/**
 * State for the "Filing check" tab.
 *
 * Same contract as the other two tabs: sorting, filtering and paging are server-side, so
 * every setter resets the page. Filters are not persisted — this is a work queue, and what
 * somebody wants on opening it is everything still outstanding.
 *
 * `enabled` gates the request on the tab actually being open. The endpoint reads two BC
 * replicas live, so it must not fire for a page that opens on "Invoices".
 */

export const FILING_CHECK_PAGE_SIZE = 50;

const SORT_FIELDS: FilingCheckSortField[] = [
    'status', 'postingDate', 'documentDate', 'company', 'bcDocumentNo',
    'vendorName', 'vendorInvoiceNo', 'orderNo', 'netAmount',
];

/** Dates and amounts read newest/largest first; text and statuses read ascending. */
const DESC_BY_DEFAULT = new Set<FilingCheckSortField>(['postingDate', 'documentDate', 'netAmount']);

const EMPTY_SUMMARY: FilingCheckSummary = {
    minPostingDate: '', companies: [], byCompany: [], postedTotals: {},
};

export function useDocuWareFilingCheck(enabled: boolean) {
    const [rows, setRows] = useState<FilingCheckRow[]>([]);
    const [summary, setSummary] = useState<FilingCheckSummary>(EMPTY_SUMMARY);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [filters, setFiltersState] = useState<FilingCheckFilters>(EMPTY_FILING_CHECK_FILTERS);
    const [sortBy, setSortBy] = useState<FilingCheckSortField>('postingDate');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [page, setPage] = useState(1);

    // Guards against a slow first request landing after a fast second one.
    const requestSeq = useRef(0);

    const load = useCallback(async () => {
        if (!enabled) return;
        const seq = ++requestSeq.current;
        setLoading(true);
        setError(null);
        try {
            const result = await docuwareFilingCheckApi.list({
                page, limit: FILING_CHECK_PAGE_SIZE, filters, sortBy, sortOrder,
            });
            if (seq !== requestSeq.current) return;
            setRows(result.items);
            setSummary(result.summary);
            setTotal(result.total);
        } catch (err) {
            if (seq !== requestSeq.current) return;
            setError(err instanceof Error ? err.message : 'Failed to load the filing check');
            setRows([]);
            setTotal(0);
        } finally {
            if (seq === requestSeq.current) setLoading(false);
        }
    }, [enabled, page, filters, sortBy, sortOrder]);

    useEffect(() => { void load(); }, [load]);

    const setFilters = useCallback((next: Partial<FilingCheckFilters>) => {
        setFiltersState(prev => ({ ...prev, ...next }));
        setPage(1);
    }, []);

    const clearFilters = useCallback(() => {
        setFiltersState(EMPTY_FILING_CHECK_FILTERS);
        setPage(1);
    }, []);

    /** Click the active column to flip direction; a new column starts at its natural one. */
    const setSort = useCallback((field: FilingCheckSortField) => {
        if (!SORT_FIELDS.includes(field)) return;
        setSortBy(prev => {
            if (prev === field) {
                setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
                return prev;
            }
            setSortOrder(DESC_BY_DEFAULT.has(field) ? 'desc' : 'asc');
            return field;
        });
        setPage(1);
    }, []);

    const activeFilterCount = Object.values(filters).filter(v => v !== '').length;
    const totalPages = Math.max(1, Math.ceil(total / FILING_CHECK_PAGE_SIZE));

    return {
        rows, summary, total, loading, error,
        filters, setFilters, clearFilters, activeFilterCount,
        sortBy, sortOrder, setSort,
        page, setPage, totalPages,
        reload: load,
    };
}
